/**
 * OAuth 2.0 PKCE utilities for TES authentication
 */

import { sha256 as jsSha256 } from 'js-sha256';

const VERIFIER_KEY = 'oauth_code_verifier';
const STATE_KEY = 'oauth_state';
const TOKENS_KEY = 'oauth_tokens';
const ENVIRONMENT_KEY = 'tes_environment';

export type TesEnvironment = 'lego-test' | 'lego-live';

/**
 * Get the TES URL for the given environment
 */
export function getTesUrl(env: TesEnvironment): string {
  return `https://${env}.api.pentatonic.com`;
}

/**
 * Store the selected TES environment
 */
export function storeEnvironment(env: TesEnvironment): void {
  localStorage.setItem(ENVIRONMENT_KEY, env);
}

/**
 * Get the selected TES environment (defaults to lego-test)
 */
export function getEnvironment(): TesEnvironment {
  const stored = localStorage.getItem(ENVIRONMENT_KEY);
  if (stored === 'lego-live' || stored === 'lego-test') {
    return stored;
  }
  return 'lego-test';
}

// Generate a cryptographically random string
function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

// Base64URL encode from Uint8Array
function base64urlEncodeBytes(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// Generate SHA-256 hash and return as base64url string
function sha256ToBase64url(plain: string): string {
  // js-sha256 returns a Uint8Array when using .array()
  const hashArray = jsSha256.array(plain);
  return base64urlEncodeBytes(new Uint8Array(hashArray));
}

/**
 * Generate a code verifier for PKCE
 */
export function generateCodeVerifier(): string {
  return generateRandomString(32);
}

/**
 * Generate a code challenge from the verifier (S256 method)
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  // Use js-sha256 which works in all contexts (including non-secure HTTP)
  return sha256ToBase64url(verifier);
}

/**
 * Generate a state parameter for CSRF protection
 */
export function generateState(): string {
  return generateRandomString(16);
}

/**
 * Store the code verifier in localStorage
 * Using localStorage instead of sessionStorage to survive cross-tab OAuth redirects
 */
export function storeVerifier(verifier: string): void {
  console.log('[OAuth] Storing verifier in localStorage, origin:', window.location.origin);
  console.log('[OAuth] Verifier (first 10 chars):', verifier.substring(0, 10));
  localStorage.setItem(VERIFIER_KEY, verifier);
  // Verify it was stored
  const stored = localStorage.getItem(VERIFIER_KEY);
  console.log('[OAuth] Verified storage:', stored ? 'success' : 'FAILED');
}

/**
 * Retrieve and clear the code verifier from localStorage
 */
export function getAndClearVerifier(): string | null {
  console.log('[OAuth] Getting verifier from localStorage, origin:', window.location.origin);
  console.log('[OAuth] All localStorage keys:', Object.keys(localStorage));
  const verifier = localStorage.getItem(VERIFIER_KEY);
  console.log('[OAuth] Retrieved verifier:', verifier ? `found (${verifier.substring(0, 10)}...)` : 'NOT FOUND');
  if (verifier) {
    localStorage.removeItem(VERIFIER_KEY);
  }
  return verifier;
}

/**
 * Store the state parameter in localStorage
 */
export function storeState(state: string): void {
  localStorage.setItem(STATE_KEY, state);
}

/**
 * Retrieve and clear the state parameter from localStorage
 */
export function getAndClearState(): string | null {
  const state = localStorage.getItem(STATE_KEY);
  localStorage.removeItem(STATE_KEY);
  return state;
}

export interface OAuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  tokenType: string;
}

/**
 * Store tokens in localStorage
 */
export function storeTokens(tokens: OAuthTokens): void {
  localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
}

/**
 * Get stored tokens from localStorage
 */
export function getTokens(): OAuthTokens | null {
  const stored = localStorage.getItem(TOKENS_KEY);
  if (!stored) return null;

  try {
    return JSON.parse(stored) as OAuthTokens;
  } catch {
    return null;
  }
}

/**
 * Clear stored tokens
 */
export function clearTokens(): void {
  localStorage.removeItem(TOKENS_KEY);
}

/**
 * Check if the access token is expired or about to expire
 */
export function isTokenExpired(tokens: OAuthTokens | null, bufferSeconds = 60): boolean {
  if (!tokens) return true;
  const now = Date.now();
  return tokens.expiresAt - bufferSeconds * 1000 <= now;
}

/**
 * Parse JWT to extract claims (without verification)
 */
export function parseJwt(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1]!;
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

export interface OAuthConfig {
  authorizeUrl: string;
  tokenUrl: string;
  clientId: string;
  redirectUri: string;
  scope: string;
}

/**
 * Build the authorization URL with PKCE parameters
 */
export async function buildAuthorizeUrl(config: OAuthConfig): Promise<string> {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateState();

  storeVerifier(verifier);
  storeState(state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    scope: config.scope,
    state,
  });

  return `${config.authorizeUrl}?${params.toString()}`;
}

/**
 * Exchange authorization code for tokens
 */
export async function exchangeCodeForTokens(
  config: OAuthConfig,
  code: string
): Promise<OAuthTokens> {
  const verifier = getAndClearVerifier();
  if (!verifier) {
    throw new Error('No code verifier found');
  }

  // Get the environment to pass to the token proxy
  const tesEnv = getEnvironment();

  const response = await fetch(config.tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Client-Id': config.clientId,
      'X-Tes-Environment': tesEnv,
    },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: config.redirectUri,
      code_verifier: verifier,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Token exchange failed: ${error}`);
  }

  const data = await response.json() as {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    token_type?: string;
  };

  const tokens: OAuthTokens = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + data.expires_in * 1000,
    tokenType: data.token_type || 'Bearer',
  };

  storeTokens(tokens);
  return tokens;
}

/**
 * Refresh the access token using the refresh token
 */
export async function refreshAccessToken(
  config: OAuthConfig,
  refreshToken: string
): Promise<OAuthTokens> {
  // Get the environment to pass to the token proxy
  const tesEnv = getEnvironment();

  const response = await fetch(config.tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Client-Id': config.clientId,
      'X-Tes-Environment': tesEnv,
    },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Token refresh failed: ${error}`);
  }

  const data = await response.json() as {
    access_token: string;
    refresh_token?: string;
    expires_in: number;
    token_type?: string;
  };

  const tokens: OAuthTokens = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token || refreshToken,
    expiresAt: Date.now() + data.expires_in * 1000,
    tokenType: data.token_type || 'Bearer',
  };

  storeTokens(tokens);
  return tokens;
}
